import { DeleteOutlined } from '@material-ui/icons';
import { Grid, Paper, Typography, Button, Dialog, DialogTitle, DialogActions, DialogContent, TextField } from '@mui/material';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import ControllerErrorAlert from '../../../../custom-components/forms/controller/ControllerErrorAlert';

const DisplayArraySx = {
    PaperSx: {
        backgroundColor: '#F7F8FB',
        width: '100%',
        border: '1px solid #E0E0E0',
        padding: (theme) => theme.spacing(2),
    },
    HeaderSx: { marginBottom: (theme) => theme.spacing(2) },
    OpenModalSx: { marginY: (theme) => theme.spacing(2) },
};

const ArrayDisplay = ({ setEdit, labels, data, modified }) => {
    const { openModalButton, noItems, display } = labels ?? {};
    return (
        <Paper sx={DisplayArraySx.PaperSx}>
            {display && (
                <Typography sx={DisplayArraySx.HeaderSx} variant="subtitle1">
                    <strong>{display}</strong>
                </Typography>
            )}
            {data && data.length > 0 ? (
                data.map((item) => <Typography variant="body1">{item}</Typography>) //
            ) : (
                <Typography variant="body1">No {noItems ?? 'items'} added yet.</Typography>
            )}
            <Button sx={DisplayArraySx.OpenModalSx} variant="outlined" color="secondary" onClick={setEdit}>
                {openModalButton ?? 'Add / Edit'}
            </Button>
            {modified && <ControllerErrorAlert errors={'Your changes have not been saved yet. Please submit the form to complete changes.'} variant="filled" severity="info" />}
        </Paper>
    );
};

const ArrayEditDialog = ({ open, setEdit, data, labels, onConfirm }) => {
    const [array, setArray] = React.useState(data ?? ['']);

    const append = (val) => {
        setArray((state) => [...state, val]);
    };
    const remove = (index) => {
        setArray((state) => state.filter((item, i) => i !== index));
    };
    const edit = (index, value) => {
        setArray((state) => {
            const newState = [...state];
            newState[index] = value;
            return newState;
        });
    };

    return (
        <Dialog maxWidth="sm" fullWidth open={open} onClose={setEdit}>
            <DialogTitle>{labels?.dialogTitle ?? 'Add/Edit Item'}</DialogTitle>
            <DialogContent>
                <Grid sx={{ marginTop: '1px' }} container rowSpacing={2}>
                    {array.map((item, index) => (
                        <>
                            <Grid item xs={11}>
                                <TextField
                                    sx={{ width: '100%' }}
                                    value={item ?? ''}
                                    label={`${labels?.fieldLabel ?? 'Item'} ${index + 1}`}
                                    onChange={(event) => {
                                        edit(index, event.target.value);
                                    }}
                                ></TextField>
                            </Grid>
                            <Grid item xs={1}>
                                <Button sx={{ height: '100%', width: '100%' }} onClick={() => remove(index)}>
                                    <DeleteOutlined color="error" />
                                </Button>
                            </Grid>
                        </>
                    ))}
                    <Grid item xs={12}>
                        <Button onClick={() => append('')} variant="outlined" color="secondary">
                            Add{labels?.addButton ? ` ${labels?.addButton}` : ''}
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={setEdit} variant="outlined" color="secondary">
                    Close
                </Button>
                <Button onClick={() => onConfirm(array)} variant="contained">
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Renders an array input field that is managed using the react-hook-form library.
 *
 * @param {Object} props - The component props.
 * @param {string} props.name - The name of the input field.
 * @param {Object} [props.labels] - The labels for the input field.
 * @param {string} [props.labels.display] - The label for the display mode.
 * @param {string} [props.labels.openModalButton] - The label for the button that opens the edit modal.
 * @param {string} [props.labels.noItems] - The label for when there are no items.
 * @param {string} [props.labels.dialogTitle] - The label for the edit modal.
 * @param {string} [props.labels.fieldLabel] - The label for the input field in the edit modal.
 * @param {string} [props.labels.addButton] - The label for the add button in the edit modal.
 * @param {Array} [props.value] - The initial value of the input field.
 * @param {Function} [props.onChange] - The function to call when the input value changes.
 * @param {Object} [props.widths] - The widths of the Grid item.
 * @param {Object} rest - The rest of the props.
 * @returns {JSX.Element} - The rendered component.
 */
export default function Array({ name, labels, widths, reference, ...rest }) {
    const {
        formState: { errors, dirtyFields },
        register,
        watch,
        setError,
        clearErrors,
        getValues,
        setValue,
        trigger,
        selectedValues,
        updateSelectedValues,
        // wrap
    } = useFormContext();
    const [editing, setEditing] = React.useState(false);
    const [modified, setModified] = React.useState(false);
    const { referenceField, referenceMap, referenceKey } = reference || {};
    useEffect(
        () => {
            if (!referenceField) {
                return;
            }
            const value = selectedValues[referenceField];
            const referenceValue = value?.[referenceKey];
            clearErrors([name]);
            setValue(name, referenceValue);
        },
        !!referenceField ? [watch(referenceField)] : []
    );

    const handleConfirm = (values) => {
        const valuesWithEmptyStringsFiltered = values.filter((item) => item !== '');
        setValue(name, valuesWithEmptyStringsFiltered);
        setModified(true);
        setEditing(false);
    };

    return (
        <Grid {...register(name)} sx={{ minWidth: '100%' }} {...widths}>
            <ArrayEditDialog
                open={editing}
                data={watch(name)}
                setEdit={() => {
                    setEditing(!editing);
                }}
                labels={labels}
                onConfirm={handleConfirm}
                setModified={setModified}
            />
            <ArrayDisplay
                labels={labels}
                setEdit={() => {
                    setEditing(!editing);
                }}
                data={watch(name)}
                modified={modified}
            />
        </Grid>
    );
}
